import {
  AloftPrice,
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  isUndefined,
  toPercentString,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCostNewByCategories,
  selectCostNewByCategory,
} from './costSelectors/selectCostNewByCategory.ts';
import { selectCompSequence, selectDepreciationSettings } from './orderSelectors.ts';
import {
  selectImprovementEntityIds,
  selectImprovementsById,
  selectImprovementsEntities,
} from './propertyDataSelectors/improvementsSelectors.ts';
import { selectCostNew } from './selectCostNew.ts';
import {
  selectDepreciationRatioByCategory,
  selectDepreciationRatiosByCategory,
} from './selectDepreciationRatioByCategory.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from './selectOrderDepreciation.ts';
import { priceJSONToPrice } from './utils/priceJSONToPrice.ts';

type CostNewByCategory = ReturnType<typeof selectCostNewByCategory>;

// Beware this calculates total depreciation as _a negative number_
const calculateDepreciation = (
  costNew: CostNewByCategory | undefined,
  ratio: ReturnType<typeof selectDepreciationRatioByCategory> | undefined,
  functionalDepreciation: AloftPrice | undefined,
  externalDepreciation: AloftPrice | undefined,
) => {
  if (isUndefined(costNew) || isUndefined(ratio)) {
    return undefined;
  }

  const physicalDepreciation = calculatePhysicalDepreciation(costNew, ratio);

  if (isUndefined(physicalDepreciation)) {
    return undefined;
  }

  const negativePhysicalDepreciation = physicalDepreciation.mul(-1);

  return negativePhysicalDepreciation.sub(externalDepreciation).sub(functionalDepreciation);
};

const calculatePhysicalDepreciation = (
  costNew: CostNewByCategory | undefined,
  ratio: ReturnType<typeof selectDepreciationRatioByCategory> | undefined,
) => {
  if (costNew === undefined || ratio === undefined) {
    return undefined;
  }

  const depreciatedCost = Object.entries(costNew).reduce(
    (acc, [category, cost]) => acc.add(cost.mul(ratio[category as keyof CostNewByCategory] ?? 0)),
    AloftPrice.zero,
  );

  return depreciatedCost.roundToDollarLike();
};

export const selectFunctionalDepreciation = createSelector(
  [selectImprovementsById, selectOrderHasFunctionalDepreciation],
  (improvements, hasFunctionalDepreciation) => {
    if (!hasFunctionalDepreciation) return AloftPrice.zero;
    return priceJSONToPrice(improvements?.functionalDepreciation);
  },
);

const selectFunctionalDepreciations = createSelector(
  [selectImprovementsEntities],
  (improvements) => {
    return Object.fromEntries(
      Object.entries(improvements).map(([propID, curr]) => [
        propID,
        priceJSONToPrice(curr.functionalDepreciation),
      ]),
    );
  },
);

export const selectFunctionalDepreciationDisplay = createSelector(
  [selectImprovementsById],
  (improvements) => {
    return priceJSONToPrice(improvements?.functionalDepreciation)?.toString(true);
  },
);

export const selectFunctionalDepreciationPercentageDisplay = createSelector(
  [selectCostNew, selectFunctionalDepreciation],
  (costNew, functionalDepreciation) => {
    if (functionalDepreciation?.gt(AloftPrice.zero) && costNew?.gt(AloftPrice.zero)) {
      return `Functional Obs ${toPercentString(functionalDepreciation.ratio(costNew), 1)}`;
    }
    return undefined;
  },
);

export const selectExternalDepreciation = createSelector(
  [selectImprovementsById, selectOrderHasExternalDepreciation],
  (improvements, hasExternalDepreciation) => {
    if (!hasExternalDepreciation) return AloftPrice.zero;
    return priceJSONToPrice(improvements?.externalDepreciation);
  },
);

const selectExternalDepreciations = createSelector([selectImprovementsEntities], (improvements) => {
  return Object.fromEntries(
    Object.entries(improvements).map(([propID, curr]) => [
      propID,
      priceJSONToPrice(curr.externalDepreciation),
    ]),
  );
});

export const selectExternalDepreciationDisplay = createSelector(
  [selectImprovementsById],
  (improvements) => {
    return priceJSONToPrice(improvements?.externalDepreciation)?.toString(true);
  },
);

export const selectExternalDepreciationPercentageDisplay = createSelector(
  [selectCostNew, selectExternalDepreciation],
  (costNew, externalDepreciation) => {
    if (externalDepreciation?.gt(AloftPrice.zero) && costNew?.gt(AloftPrice.zero)) {
      return `External Obs ${toPercentString(externalDepreciation.ratio(costNew), 1)}`;
    }
    return undefined;
  },
);

export const selectTotalEconomicLife = createSelector(
  [selectImprovementsById, selectDepreciationSettings],
  (improvements, depreciationSettings) => {
    const defaultEconomicLife =
      improvements?.propertyType === 'manufacturedHome'
        ? (depreciationSettings.defaultEcoLifeManufactured ?? DEFAULT_ECONOMIC_LIFE_MANUFACTURED)
        : (depreciationSettings.defaultEcoLife ?? DEFAULT_ECONOMIC_LIFE);
    return improvements?.totalEconomicLife ?? defaultEconomicLife;
  },
);

export const selectRemainingEconomicLife = createSelector(
  [selectTotalEconomicLife, selectImprovementsById],
  (totalEconomicLife, improvements) => {
    if (improvements?.effectiveAge === undefined || totalEconomicLife === undefined) {
      return undefined;
    }

    return totalEconomicLife - improvements.effectiveAge;
  },
);

export const selectPhysicalDepreciation = createSelector(
  [selectCostNewByCategory, selectDepreciationRatioByCategory],
  calculatePhysicalDepreciation,
);

export const selectPhysicalDepreciationDisplay = createSelector(
  [selectPhysicalDepreciation],
  (physicalDepreciation) => {
    if (isUndefined(physicalDepreciation)) {
      return undefined;
    }

    return physicalDepreciation.toString(true);
  },
);

export const selectDepreciations = createSelector(
  [
    selectCostNewByCategories,
    selectDepreciationRatiosByCategory,
    selectFunctionalDepreciations,
    selectExternalDepreciations,
    selectCompSequence,
    selectImprovementEntityIds
  ],
  (costNews, ratios, functionalDepreciations, externalDepreciations, compSequence, improvementEntityIds) => {
    if (isUndefined(costNews) || isUndefined(ratios)) {
      return undefined;
    }

    return Object.fromEntries(
      improvementEntityIds.map((propID) => [
        propID,
        calculateDepreciation(
          costNews[propID],
          ratios[propID],
          functionalDepreciations[propID],
          externalDepreciations[propID],
        ),
      ]),
    );
  },
);

export const selectDepreciation = createSelector(
  [
    selectCostNewByCategory,
    selectDepreciationRatioByCategory,
    selectFunctionalDepreciation,
    selectExternalDepreciation,
  ],
  calculateDepreciation,
);

export const selectDepreciationDisplay = createSelector([selectDepreciation], (depreciation) => {
  if (isUndefined(depreciation)) {
    return undefined;
  }

  return depreciation.toString(true);
});
